import { useState } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Search, Sparkles, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { LifecycleBadge } from "../components/shared/LifecycleWidgets";

export default function AISearchPage({ onLogout, userRole, isFeatureEnabled }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/ai-search`, { query }, { headers: getAuthHeaders() });
      setResults(resp.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
    finally { setLoading(false); }
  };

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="ai-search-page">
        <PageHeader title="AI Semantic Search" subtitle="Natural language search across all blueprints and documents" testId="ai-search-title" />
        <div className="bg-white border border-[#E2E8F0] p-6">
          <div className="flex gap-3 max-w-2xl mx-auto">
            <div className="relative flex-1">
              <Sparkles size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7C3AED]" />
              <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleSearch(); }} placeholder="Ask anything... e.g. 'find drawings related to coolant pump seals'" className="w-full pl-10 pr-4 py-3 border border-[#E2E8F0] text-sm focus:border-[#7C3AED] focus:outline-none" data-testid="ai-search-input" />
            </div>
            <Button onClick={handleSearch} disabled={loading || !query.trim()} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-none px-6 font-bold text-xs" data-testid="ai-search-btn">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <><Search size={14} className="mr-1.5" />Search</>}
            </Button>
          </div>
        </div>
        {results && (
          <div className="space-y-3" data-testid="search-results">
            {results.ai_summary && <div className="bg-[#F5F3FF] border border-[#DDD6FE] p-3 text-sm text-[#5B21B6]"><Sparkles size={14} className="inline mr-1.5" />{results.ai_summary}</div>}
            <p className="text-xs text-[#64748B]">{results.total} result(s) for "{results.query}"</p>
            {results.results.length === 0 ? <div className="bg-white border border-[#E2E8F0] p-8 text-center text-sm text-[#94A3B8]">No matching blueprints found.</div> : (
              <div className="bg-white border border-[#E2E8F0] divide-y divide-[#F1F5F9]">
                {results.results.map((r, i) => (
                  <div key={r.id} className="px-4 py-3 flex items-center gap-4 hover:bg-[#FAFAFA]" data-testid={`result-${i}`}>
                    <div className="w-10 h-10 bg-[#F5F3FF] border border-[#DDD6FE] flex items-center justify-center shrink-0"><FileText size={16} className="text-[#7C3AED]" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0F172A] truncate">{r.filename}</p>
                      <p className="text-[11px] text-[#64748B]">{r.machine_number || "—"} · {r.blueprint_type || "—"} · {r.classification || "—"}</p>
                      {r.reason && <p className="text-[10px] text-[#7C3AED] mt-0.5">{r.reason}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.lifecycle_state && <LifecycleBadge state={r.lifecycle_state} />}
                      <div className="w-12 h-12 flex flex-col items-center justify-center border" style={{ borderColor: r.score >= 70 ? "#10B981" : r.score >= 40 ? "#F59E0B" : "#E2E8F0" }}>
                        <span className="text-lg font-black" style={{ color: r.score >= 70 ? "#10B981" : r.score >= 40 ? "#F59E0B" : "#94A3B8" }}>{r.score}</span>
                        <span className="text-[8px] text-[#94A3B8] uppercase">match</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
